import { Suspense, useState } from "react";
import { Link } from "expo-router";
import { Button, Card, SliceCard } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { useMedia, View, XStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";
import {
  constructDynamicsTextSlices,
  getSliceDynamicsStatus,
} from "~/utils/construct-dynamics";
import { resolveSliceStatus } from "~/utils/get-status";
import { isNull } from "~/utils/is-null";
import { LoadingScreen } from "~/component/blocks";

type Slice = RouterOutputs["slices"]["getAllLatestResults"][number];

const GAP_BETWEEN_SLICES = 24;

const Slices = () => {
  return (
    <Suspense fallback={<LoadingScreen height={"70dvh"} />}>
      <SlicesBody />
    </Suspense>
  );
};

export default Slices;

const SlicesBody = () => {
  const { t } = useTranslation("slices");
  const { sm } = useMedia();
  const [data] = api.slices.getAllLatestResults.useSuspenseQuery();
  const [containerWidth, setContainerWidth] = useState(0);

  return (
    <Card
      data-id={"slices-results-container"}
      variant={sm ? "borderless" : "default"}
      size="sm"
      gap={"$5"}
      width={"100%"}
      $gtSm={{ size: "md" }}
    >
      <Card.Title
        data-id={"slices-results-title"}
        {...(!sm && {
          fontSize: "$7",
          lineHeight: "$7",
        })}
      >
        {t("parshall-slices-results.title")}
      </Card.Title>
      <View
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
        flexDirection={"row"}
        flexWrap="wrap"
        gap={"$3"}
        width={"100%"}
      >
        {data.map((slice) => (
          <Slice key={slice.type} width={containerWidth} {...slice} />
        ))}
      </View>
    </Card>
  );
};

// type SliceState = ComponentProps<typeof SliceCard.Score>["state"];
//
// //TODO: dynamics shouldn't have a warning state
// type SliceDynamicsState = ComponentProps<typeof SliceCard.Dynamics>["state"];

const Slice: React.FC<Slice & { width: number }> = ({
  title,
  prevCheckupDate,
  score,
  interpretation,
  dynamic,
  type,
  label,
  level,
  width,
}) => {
  const date = isNull(prevCheckupDate) ? "" : prevCheckupDate;

  const { t } = useTranslation("slices");
  const { sm } = useMedia();

  return (
    <SliceCard.Container
      data-id={"slice-card"}
      data-type={type}
      {...(sm && {
        paddingHorizontal: "$3",
        paddingVertical: "$2",
        gap: "$2",
      })}
      //TODO: remove magic numbers
      width={sm ? width : width / 2 - GAP_BETWEEN_SLICES / 2}
      justifyContent="space-between"
    >
      <SliceCard.Title font={sm ? "md" : "lg"}>{title}</SliceCard.Title>

      <View gap={"$2"} width={"100%"}>
        <View
          gap={"$1.5"}
          $sm={{
            gap: "$1",
          }}
          width={"100%"}
        >
          <XStack gap={"$2"} width={"100%"} alignItems="center">
            <SliceCard.Score
              data-id={"slice-card-score"}
              font={sm ? "sm" : "md"}
              state={resolveSliceStatus(type, level)}
            >
              {score}
            </SliceCard.Score>
            {!sm && (
              <>
                <SliceCard.Separator variant="vertical" height={"$3"} />
                <SliceCard.Dynamics
                  labelState={resolveSliceStatus(type, level)}
                  label={label}
                  description={t(
                    "parshall-slices-results.slice-dynamics-card.last-chekup",
                    {
                      lastCheckupDate: date,
                    },
                  )}
                  value={constructDynamicsTextSlices(dynamic.diff)}
                  state={getSliceDynamicsStatus(dynamic)}
                />
              </>
            )}
          </XStack>

          <SliceCard.Progress
            size={"lg"}
            rounded="lg"
            variant="success"
            value={score}
          />

          {sm && (
            <SliceCard.Dynamics
              labelState={resolveSliceStatus(type, level)}
              label={label}
              description={t(
                "parshall-slices-results.slice-dynamics-card.last-chekup",
                {
                  lastCheckupDate: date,
                },
              )}
              value={constructDynamicsTextSlices(dynamic.diff)}
              state={getSliceDynamicsStatus(dynamic)}
            />
          )}
        </View>
        <SliceCard.Description data-id={"slice-card-description"}>
          {interpretation}
        </SliceCard.Description>
      </View>
      <Link
        data-id={"link-to-slice"}
        data-type={type}
        href={`/slices/${type}`}
        asChild
      >
        <Button
          {...(!sm && {
            maxWidth: 280,
          })}
          variant="primary"
          size="md"
          rounded="lg"
        >
          {t("parshall-slices-results.slice-card.button")}
        </Button>
      </Link>
    </SliceCard.Container>
  );
};
